import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

export default function ClubHistory() {
  const { clubId } = useParams();
  const [activeTab, setActiveTab] = useState('bets');
  const [bets, setBets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, status: statusFilter, search, dateFrom, dateTo };
      if (activeTab === 'bets') {
        const res = await api.getClubBets(clubId, params);
        setBets(res.data?.bets || res.data || []);
        setTotalPages(res.data?.totalPages || 1);
      } else {
        const res = await api.getClubTransactions(clubId, params);
        setTransactions(res.data?.transactions || res.data || []);
        setTotalPages(res.data?.totalPages || 1);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setLoading(false);
  }, [clubId, activeTab, page, statusFilter, search, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [activeTab, statusFilter, search, dateFrom, dateTo]);

  const exportCsv = () => {
    const data = activeTab === 'bets' ? bets : transactions;
    if (!data.length) return;

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(v =>
      typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v
    ).join(','));
    const csv = [headers, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `club-${clubId}-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (v) => {
    const num = parseFloat(v);
    return isNaN(num) ? '$0.00' : `$${num.toFixed(2)}`;
  };

  const betStatuses = ['', 'pending', 'won', 'lost', 'Paid - Return', 'Paid - No Return', 'unresulted', 'Accepted'];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#fff', margin: 0 }}>Club History</h2>
        <button onClick={exportCsv} style={{
          padding: '8px 16px', background: '#fbbf24', color: '#000',
          border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600
        }}>Export CSV</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {['bets', 'transactions'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`history-tab ${activeTab === tab ? 'active' : ''}`} style={{
            padding: '8px 20px', border: 'none', borderRadius: 6, cursor: 'pointer',
            background: activeTab === tab ? '#fbbf24' : '#2a2d3a', color: activeTab === tab ? '#000' : '#aaa',
            fontWeight: 600
          }}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          className="history-input"
          style={{ flex: 1, minWidth: 150, padding: '8px 12px', background: '#2a2d3a', border: '1px solid #3a3d4a',
            borderRadius: 6, color: '#fff' }} />
        {activeTab === 'bets' && (
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="history-input"
            style={{ padding: '8px 12px', background: '#2a2d3a', border: '1px solid #3a3d4a',
              borderRadius: 6, color: '#fff' }}>
            {betStatuses.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <span style={{ color: '#aaa', fontSize: 12 }}>From:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="history-input"
          style={{ padding: '6px 10px', background: '#2a2d3a', border: '1px solid #3a3d4a',
            borderRadius: 6, color: '#fff', fontSize: 13 }} />
        <span style={{ color: '#aaa', fontSize: 12 }}>To:</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="history-input"
          style={{ padding: '6px 10px', background: '#2a2d3a', border: '1px solid #3a3d4a',
            borderRadius: 6, color: '#fff', fontSize: 13 }} />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="history-btn-clear"
            style={{ padding: '6px 10px', background: '#3a3d4a', border: 'none', borderRadius: 6, color: '#aaa', cursor: 'pointer', fontSize: 12 }}>
            Clear
          </button>
        )}
      </div>

      {loading ? <p style={{ color: '#aaa' }}>Loading...</p> : activeTab === 'bets' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #3a3d4a' }}>
              {['Date', 'Event', 'Stake', 'Odds', 'Status', 'Returns'].map(h =>
                <th key={h} style={{ padding: '10px 8px', textAlign: 'left', color: '#aaa', fontSize: 12 }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {bets.map((bet, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #2a2d3a' }}>
                <td style={{ padding: '10px 8px', color: '#ccc', fontSize: 13 }}>
                  {bet.created ? new Date(bet.created).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>{bet.betEventName || bet.event || '-'}</td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>{formatCurrency(bet.amount)}</td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>${bet.winPrice || bet.odds || '-'}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: ['won', 'Paid - Return', 'Return'].includes(bet.betStatus || bet.status) ? '#065f46'
                      : ['lost', 'Paid - No Return', 'No Return'].includes(bet.betStatus || bet.status) ? '#7f1d1d'
                      : '#92400e',
                    color: '#fff'
                  }}>{bet.betStatus || bet.status || 'pending'}</span>
                </td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>
                  {['won', 'Paid - Return', 'Return'].includes(bet.betStatus || bet.status)
                    ? formatCurrency((bet.amount || 0) * (bet.winPrice || 0))
                    : '-'}
                </td>
              </tr>
            ))}
            {bets.length === 0 && <tr><td colSpan={6} style={{ padding: 20, color: '#666', textAlign: 'center' }}>No bets found</td></tr>}
          </tbody>
        </table>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #3a3d4a' }}>
              {['Date', 'Type', 'Description', 'Amount', 'Status'].map(h =>
                <th key={h} style={{ padding: '10px 8px', textAlign: 'left', color: '#aaa', fontSize: 12 }}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #2a2d3a' }}>
                <td style={{ padding: '10px 8px', color: '#ccc', fontSize: 13 }}>
                  {tx.created ? new Date(tx.created).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>{tx.type || '-'}</td>
                <td style={{ padding: '10px 8px', color: '#fff' }}>{tx.description || '-'}</td>
                <td style={{ padding: '10px 8px', color: (tx.amountInCents || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                  {formatCurrency((tx.amountInCents || 0) / 100)}
                </td>
                <td style={{ padding: '10px 8px', color: '#aaa' }}>{tx.status || '-'}</td>
              </tr>
            ))}
            {transactions.length === 0 && <tr><td colSpan={5} style={{ padding: 20, color: '#666', textAlign: 'center' }}>No transactions found</td></tr>}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="history-pagination-btn"
            style={{ padding: '6px 12px', background: '#2a2d3a', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer' }}>Prev</button>
          <span style={{ padding: '6px 12px', color: '#aaa' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="history-pagination-btn"
            style={{ padding: '6px 12px', background: '#2a2d3a', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer' }}>Next</button>
        </div>
      )}
    </div>
  );
}
